import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Breadcrumb, Select } from "antd";
import { Link } from "react-router-dom";
import {
  AGENT_DAILY_COMMISSIONS_REPORT_API,
  AGENT_MONTHLY_COMMISSIONS_REPORT_API,
} from "../../../utils/apis";
import {
  GenericBarChart,
  GenericLineChart,
} from "../../../components/charts/GenericBarChart";
import { Table } from "../../../components/common/Table";
import { ExportButton } from "../../../components/common/Buttons";
import { TransactionStatusTag } from "../../../components/common/registerUtils";
import { Wrapper } from "../../../components/styled/Styled";
import { BASE_API_URL } from "../../../config";
import {
  transformDailyData,
  transformMonthlyData,
  NoWrap,
  formatDate,
  formatCurrency,
  numberFormat,
  downloadThroughAnchorLink,
} from "../../../utils/helpers";
import useGet from "../../../hooks/common/useGet";

const REPORT_TYPES = [
  { label: "Daily", value: "daily" },
  { label: "Monthly", value: "monthly" },
];

const TOTAL_TYPES = [
  { label: "Total Amount", value: "total_amount" },
  { label: "Total Count", value: "total_count" },
];

export const AgentCommissionSummary = () => {
  const {
    loadData: loadTableData,
    data: tablePaginatedData,
    loading: tableLoading,
  } = useGet();
  const {
    loadData: loadGraphData,
    data: graphData,
    loading: graphLoading,
  } = useGet();

  const [reportType, setReportType] = useState<"daily" | "monthly">("daily");
  const [totalType, setTotalType] = useState<"total_amount" | "total_count">(
    "total_amount",
  );
  const [selectedYear, setSelectedYear] = useState<string | undefined>();
  const [isDownloadingFiltered, setIsDownloadingFiltered] = useState(false);

  const [filters, setFilters] = useState({
    start_date: null as string | null,
    end_date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    loadTableData({
      api:
        reportType === "daily"
          ? AGENT_DAILY_COMMISSIONS_REPORT_API
          : AGENT_MONTHLY_COMMISSIONS_REPORT_API,
      params: {
        ...filters,
        year_value: selectedYear,
      },
    });
  }, [reportType, filters, selectedYear]);

  useEffect(() => {
    loadGraphData({
      api:
        reportType === "daily"
          ? AGENT_DAILY_COMMISSIONS_REPORT_API + "graphic-data/"
          : AGENT_MONTHLY_COMMISSIONS_REPORT_API + "graphic-data/",
      params: {
        ...filters,
        year_value: selectedYear,
      },
    });
  }, [reportType, filters, selectedYear]);

  const yearOptions = useMemo(() => {
    if (!graphData?.length) return [];

    const uniqueYears = Array.from(
      new Set(
        graphData
          .map((r: any) => (r as { year_value?: number | string }).year_value)
          .filter((y: any): y is number => typeof y === "number"),
      ),
    ) as number[];

    uniqueYears.sort((a, b) => b - a);

    return uniqueYears.map((year) => ({
      label: year.toString(),
      value: year.toString(),
    }));
  }, [graphData]);

  const chartData = useMemo(() => {
    const results = graphData || [];

    if (!results.length) {
      return { xLabels: [], series: [] };
    }

    return reportType === "daily"
      ? transformDailyData(results, totalType)
      : transformMonthlyData(results, totalType);
  }, [graphData, reportType, totalType]);

  const tableData = useMemo(() => {
    return (
      tablePaginatedData?.results?.map((item: any) => ({
        ...item,
        date: (
          <NoWrap>
            {reportType === "daily"
              ? formatDate(item.date)
              : item.month_name || item.date}
          </NoWrap>
        ),
        total_amount: formatCurrency(item.total_amount || "0"),
        total_count: numberFormat(item.total_count || "0"),
        status: <TransactionStatusTag status={item.status} />,
      })) || []
    );
  }, [tablePaginatedData?.results, reportType]);

  const tableColumns = useMemo(
    () => [
      { id: "date", header: "Date" },
      { id: "total_amount", header: "Total Amount", align: "right" },
      { id: "total_count", header: "Total Count" },

      ...(reportType === "daily"
        ? [
            { id: "day_of_month", header: "Day", filter: { type: "number" } },
            { id: "month_name", header: "Month", filter: { type: "text" } },
          ]
        : []),
      { id: "year_value", header: "Year", filter: { type: "text" } },
    ],
    [reportType],
  );

  const hasNoData = !graphLoading && (!graphData || graphData.length === 0);

  const reportTitle =
    reportType === "daily"
      ? "Daily Transaction Summary"
      : "Monthly Transaction Summary";

  const EXPORT_ENDPOINTS = {
    daily: AGENT_DAILY_COMMISSIONS_REPORT_API + "export-to-csv",
    monthly: AGENT_MONTHLY_COMMISSIONS_REPORT_API + "export-to-csv",
  };
  const handleDownloadFiltered = () => {
    setIsDownloadingFiltered(true);

    const endpoint = EXPORT_ENDPOINTS[reportType];

    downloadThroughAnchorLink(
      `${BASE_API_URL}${endpoint}`,
      `agent-${reportType}-commission-summary.xlsx`,
      {
        ...filters,
        year_value: selectedYear,
        report_type: reportType,
      },
    ).finally(() => {
      setIsDownloadingFiltered(false);
    });
  };

  return (
    <Wrapper>
      <div className="p-4 flexColumn gap20">
        <HeaderWrapper>
          <Breadcrumb
            items={[
              { title: <Link to="/">Home</Link> },
              { title: reportTitle },
            ]}
          />
        </HeaderWrapper>

        <div className="whiteBg p-4" style={{ borderRadius: 10 }}>
          <FilterRow style={{ marginBottom: "30px" }}>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "5px" }}
            >
              <label style={{ fontSize: "14px" }}>Report Type</label>
              <Select
                options={REPORT_TYPES}
                value={reportType}
                onChange={(value) => {
                  setReportType(value);
                  setSelectedYear(undefined);
                }}
                style={{ width: 140 }}
              />
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "5px" }}
            >
              <label style={{ fontSize: "14px" }}>Filter by Year</label>
              <Select
                placeholder="All years"
                options={yearOptions}
                value={selectedYear}
                onChange={setSelectedYear}
                allowClear
                style={{ width: 140 }}
                loading={tableLoading}
              />
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "5px" }}
            >
              <label style={{ fontSize: "14px" }}>Total Type</label>
              <Select
                options={TOTAL_TYPES}
                value={totalType}
                onChange={(value) => setTotalType(value)}
                style={{ width: 160 }}
              />
            </div>
          </FilterRow>

          {hasNoData ? (
            <EmptyState>
              No transaction data available for the selected filters
            </EmptyState>
          ) : (
            <ChartsWrapper>
              <GenericBarChart
                data={chartData.series}
                title={reportTitle}
                xLabels={chartData.xLabels}
                yLabel={
                  totalType === "total_amount" ? "Total Amount" : "Total Count"
                }
                loading={graphLoading}
                height={380}
              />
              <GenericLineChart
                data={chartData.series}
                title={`${reportTitle} Trend`}
                xLabels={chartData.xLabels}
                yLabel={
                  totalType === "total_amount" ? "Total Amount" : "Total Count"
                }
                loading={graphLoading}
                height={380}
              />
            </ChartsWrapper>
          )}
        </div>

        <Table
          loading={tableLoading}
          count={tablePaginatedData?.count || 0}
          tableColumns={tableColumns}
          tableData={tableData}
          filters={filters}
          onFilterChange={setFilters}
          noEmptyText={false}
          tableTitle={reportTitle}
          tableHeaderItem={
            <div className="flexRow gap10">
              <ExportButton
                text="Export CSV"
                onClick={handleDownloadFiltered}
                isDownloading={isDownloadingFiltered}
              />
            </div>
          }
        />
      </div>
    </Wrapper>
  );
};

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: end;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ChartsWrapper = styled.div`
  display: flex;
  gap: 20px;
  border-radius: 10px;
  padding: 16px;
  background-color: #fff;
  flex-wrap: wrap;

  & > * {
    flex: 1 1 45%;
    min-width: 300px;
  }

  @media (max-width: 1024px) {
    & > * {
      flex: 1 1 100%;
    }
  }
`;

const EmptyState = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #8c8c8c;
  background: #fafafa;
  border-radius: 10px;
  border: 1px dashed #d9d9d9;
`;
